
import { Badge, StudyStats, User } from '../types';

const STATS_KEY = 'cna_user_gamification_v1';

const INITIAL_STATS: StudyStats = {
  quizzesTaken: 0,
  averageScore: 0,
  studyHours: 0,
  topicsMastered: 0,
  points: 0,
  level: 1,
  badges: [],
  xpToday: 0,
  dailyXPGoal: 500,
  streakShields: 0,
  lastXPUpdate: new Date().toISOString()
};

const BADGE_MANIFEST: Record<string, Omit<Badge, 'dateEarned'>> = {
  'FIRST_STEPS': { id: 'FIRST_STEPS', name: 'First Steps', description: 'Complete your first practice session.', icon: '👣' },
  'EXAM_SURVIVOR': { id: 'EXAM_SURVIVOR', name: 'Exam Survivor', description: 'Complete a full length exam simulation.', icon: '🛡️' },
  'PERFECT_SCORE': { id: 'PERFECT_SCORE', name: 'Clinical Excellence', description: 'Score 100% on a practice quiz.', icon: '✨' },
  'NIGHT_OWL': { id: 'NIGHT_OWL', name: 'Night Owl', description: 'Study after 10 PM.', icon: '🦉' },
  'CONSISTENCY_KING': { id: 'CONSISTENCY_KING', name: 'Consistency King', description: 'Study 3 days in a row.', icon: '👑' },
  'TOPIC_MASTER_SAFETY': { id: 'TOPIC_MASTER_SAFETY', name: 'Safety Master', description: 'Master the Safety & Emergency domain.', icon: '🚧' },
  'SYNAPSE_MASTER': { id: 'SYNAPSE_MASTER', name: 'Synapse Master', description: 'Correctly recalled 50 unique clinical flashcards.', icon: '🧠' }
};

export const GamificationService = {
  getStats: (): StudyStats => {
    try {
      const stored = localStorage.getItem(STATS_KEY);
      return stored ? JSON.parse(stored) : INITIAL_STATS;
    } catch (e) {
      return INITIAL_STATS;
    }
  },

  addXP: (currentStats: StudyStats, amount: number): StudyStats => {
    const updated = { ...currentStats };
    const now = new Date();
    const lastUpdate = new Date(updated.lastXPUpdate || now.toISOString());
    
    // Reset daily XP if it's a new day
    if (now.toDateString() !== lastUpdate.toDateString()) {
        updated.xpToday = 0;
    }

    updated.points = (updated.points || 0) + amount;
    updated.xpToday = (updated.xpToday || 0) + amount;
    updated.level = Math.floor(updated.points / 500) + 1;
    updated.lastXPUpdate = now.toISOString();
    updated.dailyXPGoal = updated.dailyXPGoal || 500;
    updated.streakShields = updated.streakShields || 0;
    
    return updated;
  },

  checkBadges: (user: User | null, sessionStats: { examsTaken: number, lastScore: number, level: number, emergencyScore: number, flashcardsMastered?: number }): Badge[] => {
    const newBadges: Badge[] = [];
    const userBadges = user?.stats?.badges || [];
    const currentBadgeIds = new Set(userBadges.map((b: Badge) => b.id));
    const now = new Date().toLocaleDateString();

    if (!currentBadgeIds.has('FIRST_STEPS') && sessionStats.examsTaken >= 1) {
        newBadges.push({ ...BADGE_MANIFEST['FIRST_STEPS'], dateEarned: now } as Badge);
    }
    if (!currentBadgeIds.has('EXAM_SURVIVOR') && sessionStats.examsTaken >= 5) {
        newBadges.push({ ...BADGE_MANIFEST['EXAM_SURVIVOR'], dateEarned: now } as Badge);
    }
    if (!currentBadgeIds.has('PERFECT_SCORE') && sessionStats.lastScore === 100) {
        newBadges.push({ ...BADGE_MANIFEST['PERFECT_SCORE'], dateEarned: now } as Badge);
    }
    if (!currentBadgeIds.has('TOPIC_MASTER_SAFETY') && sessionStats.emergencyScore >= 90) {
        newBadges.push({ ...BADGE_MANIFEST['TOPIC_MASTER_SAFETY'], dateEarned: now } as Badge);
    }
    if (!currentBadgeIds.has('SYNAPSE_MASTER') && (sessionStats.flashcardsMastered || 0) >= 50) {
        newBadges.push({ ...BADGE_MANIFEST['SYNAPSE_MASTER'], dateEarned: now } as Badge);
    }
    
    return newBadges;
  },

  addPoints: (amount: number) => {
    const stats = GamificationService.getStats();
    stats.points += amount;
    
    const nextLevel = Math.floor(stats.points / 500) + 1;
    if (nextLevel > stats.level) {
      stats.level = nextLevel;
    }
    
    localStorage.setItem(STATS_KEY, JSON.stringify(stats));
    window.dispatchEvent(new CustomEvent('gamification-updated'));
    return stats;
  },

  awardBadge: (badgeId: keyof typeof BADGE_MANIFEST) => {
    const stats = GamificationService.getStats();
    if (stats.badges.find(b => b.id === badgeId)) return;

    const badgeData = BADGE_MANIFEST[badgeId];
    const newBadge: Badge = {
      ...badgeData,
      dateEarned: new Date().toLocaleDateString()
    };

    stats.badges.push(newBadge);
    localStorage.setItem(STATS_KEY, JSON.stringify(stats));
    window.dispatchEvent(new CustomEvent('gamification-updated'));
    return newBadge;
  },

  getLeaderboard: () => {
    return [
      { name: 'Sarah M.', points: 4250, level: 9, rank: 1 },
      { name: 'James K.', points: 3890, level: 8, rank: 2 },
      { name: 'You', points: GamificationService.getStats().points, level: GamificationService.getStats().level, rank: 3, isUser: true },
      { name: 'Elena R.', points: 3100, level: 7, rank: 4 },
      { name: 'Michael T.', points: 2850, level: 6, rank: 5 }
    ];
  },

  calculateStreak: (lastActiveDate?: string, currentStreak: number = 0): { streak: number, updated: boolean } => {
    if (!lastActiveDate) return { streak: 1, updated: true };
    
    const now = new Date();
    const last = new Date(lastActiveDate);
    
    // Set to midnight for day comparison
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const lastDate = new Date(last.getFullYear(), last.getMonth(), last.getDate());

    if (lastDate.getTime() === today.getTime()) {
      return { streak: currentStreak || 1, updated: false };
    }

    if (lastDate.getTime() === yesterday.getTime()) {
      return { streak: (currentStreak || 0) + 1, updated: true };
    }

    // Streak broken
    return { streak: 1, updated: true };
  }
};
